/**
 * Detect when a page specific javascript module has loaded
 *
 * @module page-modules
 * @author Aaron Layton <aaron.layton@remarkable.net>
 */

/**
 * The below code will complete if the module is already loaded
 * wait for a script tag with `.pageJS` to load and check again
 * when the Window.load event fires
 * and finally after 10 seconds
 * @static
 *
 * @property {CSSSelector}  pageModules.scriptElement       - The default CSS selector for the script tags to listen to.
 * @property {number}  pageModules.scriptTimeout       - The default timeout duration in ms.
 */
var pageModules = {
  version: '1.0.0',
  scriptElement: '.pageJS',
  scriptTimeout: 10000
};

/**
 * Decides weather to settle or reject the given methods based on a module being loaded in the window
 *
 * @private
 * @function
 * @param {string} section - name of the section
 * @param {callback} resolve - pass in the resolve method of the promise
 * @param {callback} reject - pass in the reject method of the promise
 *
 * @returns {Promise} returns promise callback
 */
export const _settlePromise = (section, resolve, reject) => {
  var succeedMessage = section + ' loaded',
    failMessage = section + ' not loaded';

  return isLoaded(section) ? resolve(succeedMessage) : reject(failMessage);
};

/**
 * Returns true if the section name has a key against the window object
 *
 * @name isLoaded
 * @static
 * @function
 * @param {string} section - name of the section
 *
 * @returns {bool} bool representing the state of the loaded section
 *
 * @example
 * const pageModule = "homepageController";
 *
 * const homepageLoaded = pageModules.isLoaded(pageModule);
 *
 * if (homepageLoaded) {
 *      console.log("homepage is ready");
 * }
 */
export const isLoaded = (section) =>
  section in window && (typeof window[section].init === 'function' || typeof window[section] === 'function');

/**
 *
 *
 * @name awaitLoad
 * @static
 * @function
 * @param {string} section - name of the section
 * @param {CSSSelector} [elmId='.pageJS']  - optional override selector
 *
 * @returns {Promise} resolves when it detects the js module for the section has loaded
 * @example
 * const pageModule = "homepageController";
 *
 * const pageCode = pageModules.awaitLoad(pageModule).then(function (result) {
 *     console.log(result);
 *     window[pageModule].init();
 * }, function (err) {
 *     console.log(err);
 * });
 *
 * @example
 * // Changes the script timeout
 * pageModules.scriptTimeout = 5000;
 *
 * @example
 * // Watching a different Script tag
 * // You can watch a different script tag by passing it in as an optional second parameter
 * pageModules.awaitLoad(pageModule, 'secondPageJs');
 */
export const awaitLoad = (section, elmId) => {
  var timerObject,
    scriptElement = elmId || pageModules.scriptElement;

  return new Promise(function (resolve, reject) {
    if (isLoaded(section)) {
      _settlePromise(section, resolve, reject);
    } else {
      timerObject = setTimeout(function () {
        return _settlePromise(section, resolve, reject);
      }, pageModules.scriptTimeout);

      // Get the last node and wait for that to complete

      var nodes = document.querySelectorAll(scriptElement);
      if (nodes.length) {
        var scriptNode = nodes[nodes.length - 1];

        if (scriptNode !== null) {
          scriptNode.addEventListener('load', function () {
            clearTimeout(timerObject);
            return _settlePromise(section, resolve, reject);
          });
        }
      }

      window.addEventListener('load', function () {
        clearTimeout(timerObject);
        return _settlePromise(section, resolve, reject);
      });
    }
  });
};